import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  BookOpen, Lock, Smartphone, ShieldCheck, CloudUpload, 
  AlertTriangle, Eye, Wifi, RefreshCw, User, Globe, Users,
  Home, Award, Menu, X, CheckCircle
} from 'lucide-react';
import { courseModules } from '../../data/courseData';
import { useProgress } from '../../context/ProgressContext';
import { cn } from '../../lib/utils';

const iconMap = {
  BookOpen, Lock, Smartphone, ShieldCheck, CloudUpload,
  AlertTriangle, Eye, Wifi, RefreshCw, User, Globe, Users,
};

export function Sidebar({ isOpen, onToggle }) {
  const location = useLocation();
  const { progress, getOverallProgress, isModuleComplete } = useProgress();
  const overallProgress = getOverallProgress();

  return (
    <>
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-toggle"
        onClick={onToggle}
        className="fixed top-4 left-4 z-50 md:hidden bg-primary text-white p-3 rounded-xl shadow-lg hover:bg-primary-600 transition-colors"
        aria-label={isOpen ? "Close menu" : "Open menu"}
      >
        {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed md:sticky top-0 left-0 h-screen w-72 bg-gradient-to-b from-primary to-primary-700 text-white z-40 transition-transform duration-300 ease-in-out overflow-hidden flex flex-col",
          isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3 group" onClick={onToggle}>
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-md group-hover:scale-105 transition-transform overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_aussiesafety/artifacts/17uanq9m_BTA_Logo_NoBgnd.png" 
                alt="Bellarine Tech Assist Logo"
                className="w-10 h-10 object-contain"
              />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg leading-tight">Digital Safety</h1>
              <p className="text-primary-100 text-sm">by Bellarine Tech Assist</p>
            </div>
          </Link>
        </div>

        {/* Progress */}
        <div className="px-6 py-4 border-b border-white/10">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-primary-100">Your Progress</span>
            <span className="font-bold text-secondary">{overallProgress}%</span>
          </div>
          <div className="h-3 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-secondary rounded-full transition-all duration-500 ease-out"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
          <p className="text-xs text-primary-100 mt-2">
            {progress.completedModules.length} of {courseModules.length} modules complete
          </p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <Link
            to="/"
            data-testid="nav-home"
            onClick={onToggle}
            className={cn(
              "flex items-center gap-3 px-4 py-3 rounded-xl mb-2 transition-all",
              location.pathname === "/" 
                ? "bg-white/20 border-l-4 border-secondary" 
                : "hover:bg-white/10"
            )}
          >
            <Home className="w-5 h-5" />
            <span className="font-medium">Home</span>
          </Link>

          <div className="text-xs uppercase tracking-wider text-primary-200 px-4 py-2 mt-2">
            Modules
          </div>

          {courseModules.map((module) => {
            const Icon = iconMap[module.icon] || BookOpen;
            const isComplete = isModuleComplete(module.id);
            const isActive = location.pathname === `/module/${module.slug}`;
            
            return (
              <Link
                key={module.id}
                to={`/module/${module.slug}`}
                data-testid={`nav-module-${module.slug}`}
                onClick={onToggle}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl mb-1 transition-all group",
                  isActive 
                    ? "bg-white/20 border-l-4 border-secondary" 
                    : "hover:bg-white/10"
                )}
              >
                <div className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                  isComplete ? "bg-secondary" : "bg-white/10 group-hover:bg-white/20"
                )}>
                  {isComplete ? (
                    <CheckCircle className="w-5 h-5 text-secondary-foreground" />
                  ) : (
                    <Icon className="w-5 h-5" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-sm truncate block">
                    {module.id}. {module.shortTitle}
                  </span>
                </div>
              </Link>
            );
          })}

          <div className="border-t border-white/10 mt-4 pt-4">
            <Link
              to="/resources"
              data-testid="nav-resources"
              onClick={onToggle}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl mb-2 transition-all",
                location.pathname === "/resources" 
                  ? "bg-white/20 border-l-4 border-secondary" 
                  : "hover:bg-white/10"
              )}
            >
              <Globe className="w-5 h-5" />
              <span className="font-medium">Resources</span>
            </Link>

            {overallProgress === 100 && (
              <Link
                to="/certificate"
                data-testid="nav-certificate"
                onClick={onToggle}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-all",
                  location.pathname === "/certificate" 
                    ? "bg-white/20 border-l-4 border-secondary" 
                    : "hover:bg-white/10 bg-secondary/20"
                )}
              >
                <Award className="w-5 h-5 text-secondary" />
                <span className="font-medium text-secondary">Certificate</span>
              </Link>
            )}
          </div>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 text-center">
          <p className="text-xs text-primary-200">
            Based on the Digital Safety Workbook
          </p>
          <p className="text-xs text-primary-300 mt-1">
            by Bellarine Tech Assist
          </p>
        </div>
      </aside>
    </>
  );
}
